// docs/101 KS4: a kill streak called out over the hero who made the kill: the announcer's word
// pops up over its head and confetti bursts round it, more the longer the streak. Drawn on this
// client only; the server and the other players never see it.
import type Phaser from 'phaser';
import type { StreakKill } from '../../core/encounter/streak';
import type { Tile } from '../../core/sim/types';
import { oneShot } from './oneShots';

export interface StreakFxHost {
  scene: Phaser.Scene;
  cell: number;
  dpr(): number;
  /** The board's effects layer (world space): the confetti flies there. */
  addEffect(o: Phaser.GameObjects.GameObject): void;
  /** The overlay (screen space, as the floating numbers): the word sits there, crisp at any zoom. */
  addOverlay(o: Phaser.GameObjects.GameObject): void;
  /** The centre of a square in world space. */
  tileCentre(t: Tile): { x: number; y: number };
  /** The zoom the overlay is drawn at (world px → overlay px). */
  zoom(): number;
  /** Scales a duration by the reduce-motion setting (0 when motion is off). */
  motionMs(ms: number): number;
}

/** The word's colour climbs with the streak: gold, amber, ember, crimson, violet. */
const TIERS = [
  { from: 1, fill: '#ffd166', confetti: [0xffd166, 0xfff1b8, 0x6fe08a, 0x7ac7ff] },
  { from: 3, fill: '#ffb347', confetti: [0xffb347, 0xffd166, 0xff6f91, 0x7ac7ff, 0x6fe08a] },
  { from: 5, fill: '#ff7a45', confetti: [0xff7a45, 0xffd166, 0xff3d6e, 0xb388ff, 0x4fe3c1] },
  { from: 7, fill: '#ff4d6d', confetti: [0xff4d6d, 0xffd166, 0xb388ff, 0x4fc3ff, 0x6fe08a, 0xffffff] },
  { from: 9, fill: '#d58cff', confetti: [0xd58cff, 0xff4d6d, 0xffd166, 0x4fc3ff, 0x6fe08a, 0xffffff] },
];
const tierOf = (count: number) => [...TIERS].reverse().find((t) => count >= t.from)!;

/** Pieces of confetti for the `count`th kill in a row: a handful for the first, a storm by the eighth. */
export const confettiFor = (count: number): number => Math.min(160, 12 + 16 * (count - 1));

const PIECE = 'fk-confetti';

export class StreakFx {
  constructor(private readonly host: StreakFxHost) {}

  /** Calls out `kill` over `tile` (the killer's square, the top-left of a huge one) `size` squares wide. */
  celebrate(kill: StreakKill, tile: Tile, size: number): void {
    const head = this.host.tileCentre(size > 1 ? { x: tile.x + (size - 1) / 2, y: tile.y } : tile);
    head.y -= this.host.cell / 2;
    this.word(kill, head);
    if (this.host.motionMs(1) > 0) this.confetti(kill.count, head);
  }

  private word(kill: StreakKill, at: { x: number; y: number }): void {
    const { scene } = this.host;
    const z = Math.min(4, Math.max(2, this.host.zoom()));
    const tier = tierOf(kill.count);
    // a little bigger with every rung, up to half again
    const size = Math.round(11 * z * Math.min(1.5, 1 + (kill.count - 1) * 0.07));
    const sx = Math.round(at.x * this.host.zoom()), sy = Math.round(at.y * this.host.zoom()) - Math.round(26 * z);
    const txt = scene.add.text(sx, sy, kill.call.words.toUpperCase(), {
      fontFamily: 'Cinzel, Georgia, serif', fontSize: `${size}px`, fontStyle: '900', color: tier.fill, stroke: '#0b0f1a', strokeThickness: 5,
    }).setOrigin(0.5, 1).setResolution(this.host.dpr());
    txt.setShadow(0, 2, '#000000', 0, true, false);
    this.host.addOverlay(txt);
    const m = this.host.motionMs;
    if (m(1) === 0) {
      scene.time.delayedCall(1400, () => txt.destroy());
      return;
    }
    txt.setScale(0.2).setAlpha(0);
    oneShot(scene, txt);
    scene.tweens.chain({ targets: txt, tweens: [
      { scale: 1.3, alpha: 1, duration: m(170), ease: 'Back.easeOut' },
      { scale: 1, duration: m(130), ease: 'Quad.easeOut' },
      { y: sy - 10, duration: m(900), ease: 'Sine.easeOut' },
      { y: sy - 22, alpha: 0, duration: m(320), ease: 'Quad.easeIn' },
    ], onComplete: () => txt.destroy() });
  }

  private confetti(count: number, at: { x: number; y: number }): void {
    const { scene, cell } = this.host;
    if (!scene.textures.exists(PIECE)) {
      const g = scene.make.graphics({}, false);
      g.fillStyle(0xffffff, 1).fillRect(0, 0, 6, 3);
      g.generateTexture(PIECE, 6, 3);
      g.destroy();
    }
    const tier = tierOf(count);
    const pieces = confettiFor(count);
    const burst = (n: number, delay: number) => scene.time.delayedCall(delay, () => {
      const e = scene.add.particles(0, 0, PIECE, {
        emitting: false, lifespan: { min: 900, max: 1500 },
        speed: { min: cell * 2.5, max: cell * (5 + Math.min(count, 8) * 0.6) }, angle: { min: -150, max: -30 },
        // each piece tumbles its own way, faster the faster it flies sideways
        rotate: { onEmit: () => Math.random() * 360, onUpdate: (p: Phaser.GameObjects.Particles.Particle, _k: string, _t: number, v: number) => v + p.velocityX * 0.04 },
        scale: { min: cell / 22, max: cell / 13 }, alpha: { start: 1, end: 0, ease: 'Quad.easeIn' },
        tint: tier.confetti, gravityY: cell * 9,
      });
      this.host.addEffect(e);
      e.explode(n, at.x, at.y);
      scene.time.delayedCall(1700, () => e.destroy());
    });
    // a longer streak bursts again, and again: a storm, not one pop
    const waves = count >= 6 ? 3 : count >= 3 ? 2 : 1;
    for (let w = 0; w < waves; w++) burst(Math.ceil(pieces / waves), w * 180);
  }
}
